import { Car, Clock, ExternalLink, Fish, MapPin, Utensils } from "lucide-react";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/motion/Reveal";
import { Stagger, StaggerItem } from "../components/motion/Stagger";
import { restaurants, siteInfo } from "../data/site";

const localGuideHighlights = [
  { icon: MapPin, label: "Walkable village essentials" },
  { icon: Fish, label: "East Branch fly fishing" },
  { icon: Car, label: "Route 17 / future I-86 access" },
];

export function LocalGuide() {
  return (
    <>
      <SEO
        title="Local Guide | The Deposit Seed House"
        description="Restaurants, grocery options, directions, and fly-fishing context near The Deposit Seed House in Deposit, NY."
        image="/assets/local/anglers.jpg"
        canonicalPath="/local-guide"
      />
      <section className="page-hero page-hero--local">
        <div>
          <p className="eyebrow">Local guide</p>
          <h1>Small-town essentials near the Delaware River.</h1>
          <p>Plan the practical parts of your stay: food, directions, parking, and quick access to the East Branch of the Delaware River.</p>
          <div className="page-hero__stats" aria-label="Local guide summary">
            {localGuideHighlights.map((item) => {
              const Icon = item.icon;

              return (
                <span key={item.label}>
                  <Icon size={17} aria-hidden="true" />
                  {item.label}
                </span>
              );
            })}
          </div>
          <div className="page-hero__actions">
            <a className="button button--primary" href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
              Open map <ExternalLink size={16} aria-hidden="true" />
            </a>
            <Link className="button button--light" to="/contact">
              Ask about the area
            </Link>
          </div>
        </div>
      </section>
      <section className="section local-guide-section">
        <div className="local-guide-intro">
          <div>
            <p className="eyebrow">Stay orientation</p>
            <h2>Simple logistics for a Deposit stay.</h2>
          </div>
          <div className="local-guide-intro__cards">
            <span>
              <Clock size={18} aria-hidden="true" />
              Check-in {siteInfo.checkIn}
            </span>
            <span>
              <Clock size={18} aria-hidden="true" />
              Check-out {siteInfo.checkOut}
            </span>
            <span>
              <MapPin size={18} aria-hidden="true" />
              Rear parking area
            </span>
          </div>
        </div>
        <Stagger className="guide-grid">
          <StaggerItem as="article" className="guide-block">
            <span className="feature__icon">
              <Utensils size={24} aria-hidden="true" />
            </span>
            <h2>Nearby restaurants</h2>
            <ul>
              {restaurants.map((restaurant) => (
                <li key={restaurant}>{restaurant}</li>
              ))}
            </ul>
            <p>Breweries are located in Hancock and Binghamton, and there is a liquor store in town.</p>
          </StaggerItem>
          <StaggerItem as="article" className="guide-block">
            <span className="feature__icon">
              <Fish size={24} aria-hidden="true" />
            </span>
            <h2>Fly fishing</h2>
            <p>The East Branch of the Delaware River is known for world-class fly fishing and easy access from Deposit.</p>
            <img src="/assets/local/anglers.jpg" alt="Anglers fly fishing near Deposit, New York" loading="lazy" />
          </StaggerItem>
          <StaggerItem as="article" className="guide-block">
            <span className="feature__icon">
              <MapPin size={24} aria-hidden="true" />
            </span>
            <h2>Directions</h2>
            <p>{siteInfo.address}</p>
            <p>A large rear parking area is served by two driveways and can accommodate normal vehicles and small trailers.</p>
            <a className="button button--secondary" href={siteInfo.mapUrl} target="_blank" rel="noreferrer">
              Open map <ExternalLink size={16} aria-hidden="true" />
            </a>
          </StaggerItem>
        </Stagger>
      </section>
      <Reveal as="section" className="section local-pullquote">
        <blockquote className="pull-quote">
          Some of the finest wild-trout water in the Northeast runs cold and clear past Deposit.
        </blockquote>
      </Reveal>
    </>
  );
}
